import React, { useEffect, useState } from "react";
import { LineChart } from "lucide-react";
import { getMarketOverview } from "../lib/api";
import { MarketRegime } from "../components/MarketRegime";
import { MarketChart } from "../components/MarketChart";
import { IndexTile } from "../components/IndexTile";
import { IntelligencePanels } from "../components/IntelligencePanels";
import { Panel, SectionHeading } from "../components/Panel";
import { Reveal } from "../components/Reveal";
import { StaggerGrid, StaggerItem } from "../components/Motion";
import { formatNum, formatPercent, signColor } from "../lib/format";
import { DataState } from "../components/DataState";
import { SkeletonBlock } from "../components/LoadingSkeleton";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const Ticker = ({ indices }) => (
  <div className="mb-5 overflow-hidden rounded-lg border border-white/[0.07] bg-[#0B0C0E]">
    <div className="lv-marquee-track py-2">
      {[...indices, ...indices].map((i, k) => (
        <span key={k} className="mx-6 inline-flex items-center gap-2 font-mono text-xs">
          <span className="font-semibold text-zinc-300">{i.name}</span>
          <span className="text-white tabular-nums">{formatNum(i.value)}</span>
          <span className={signColor(i.change_percent)}>{formatPercent(i.change_percent)}</span>
        </span>
      ))}
    </div>
  </div>
);

export default function Markets() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = () => { setError(false); getMarketOverview().then(setData).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  if (error) return <DataState onRetry={load} />;
  if (!data) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="kicker mb-1.5">Command Centre · India</div>
          <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">What's happening in the Indian market</h1>
        </div>
        <Link
          to="/discover"
          className="lv-press inline-flex w-fit items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2 font-mono text-xs font-medium text-amber-300 transition-colors hover:bg-amber-500/20"
        >
          Explore companies <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>

      <Ticker indices={data.indices} />

      {/* Regime + Chart */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <MarketRegime regime={data.regime} glossary={data.glossary} />
        </div>
        <div className="lg:col-span-8">
          <Panel topEdge className="h-full p-5">
            <MarketChart />
          </Panel>
        </div>
      </div>

      {/* Index snapshot */}
      <Reveal>
        <SectionHeading kicker="Index Snapshot" title="Key Indian Indices" icon={LineChart} />
        <StaggerGrid className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {data.indices.map((idx) => <StaggerItem key={idx.id}><IndexTile idx={idx} /></StaggerItem>)}
        </StaggerGrid>
      </Reveal>

      {/* Market intelligence */}
      <Reveal>
        <SectionHeading kicker="Market Intelligence" title="Reading the tape" />
        <IntelligencePanels intel={data.intelligence} />
      </Reveal>
    </div>
  );
}

export function PageLoading() {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <SkeletonBlock className="h-[420px] lg:col-span-4" />
      <SkeletonBlock className="h-[420px] lg:col-span-8" />
    </div>
  );
}
